/**
 * Conservative client-side backstop for severe user-authored abuse.
 *
 * The API remains the primary enforcement layer. This duplicate guard keeps
 * historical unsafe application copy out of request UIs even when an older
 * record used spacing, punctuation, or concatenation to evade server-side
 * token matching.
 */

export const SEVERE_ABUSE_REDACTION = '[Content hidden due to severe abuse]';

const ZERO_WIDTH_CHARACTERS = /[\u200b\u200c\u200d\u2060\ufeff]/g;
const LEET_CHARACTERS = /[013457@$!|]/g;
const LEET_TRANSLATION: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '@': 'a',
  '$': 's',
  '!': 'i',
  '|': 'i',
};

// Exact normalized tokens only. Ordinary disagreement and standalone
// profanity remain allowed; this list is reserved for severe abuse.
const SEVERE_SLUR_TOKENS = new Set([
  'chink',
  'faggot',
  'fagot',
  'kike',
  'nigger',
  'nigga',
  'raghead',
  'retard',
  'spic',
  'tranny',
  'wetback',
]);

const TARGETED_THREAT_PATTERNS = [
  /\b(?:i|we)\s+(?:am\s+going\s+to|will|wanna|want\s+to)\s+(?:kill|murder|shoot|stab|lynch|rape|hurt|attack|beat)\s+(?:you|him|her|them)\b/,
  /\b(?:kill|murder|shoot|stab|lynch|rape|hurt|attack|beat)\s+(?:you|him|her|them)\b/,
  /\b(?:kill\s+yourself|go\s+die|you\s+deserve\s+to\s+die)\b/,
];

// Catch narrowly targeted profanity, including the historical "yo u"
// spacing evasion, without treating standalone profanity as severe abuse.
const TARGETED_PROFANITY_PATTERNS = [
  /\bfuck(?:ing)?\s+(?:y\s*o\s*u|u)\b/,
  /\bfuck(?:ing)?(?:you|u)\b/,
  /\bgo\s+fuck\s+(?:y\s*o\s*u(?:rself)?|u)\b/,
];

const normalizeForMatching = (value: string) => {
  const normalized = value
    .normalize('NFKC')
    .replace(ZERO_WIDTH_CHARACTERS, '')
    .trim()
    .toLowerCase()
    .replace(LEET_CHARACTERS, character => LEET_TRANSLATION[character] || character);

  const sentence = normalized.replace(/[^a-z0-9]+/g, ' ').trim();
  const words = (normalized.match(/[a-z0-9._*-]+/g) || [])
    .map(chunk => chunk.replace(/[^a-z0-9]/g, ''))
    .filter(Boolean);

  return { sentence, words };
};

const isJoinedSevereSlur = (word: string): boolean => {
  for (const slur of Array.from(SEVERE_SLUR_TOKENS)) {
    if (!word.includes(slur)) continue;

    // Handle deliberate joins such as "<slur>fuckyou" and repeated slurs,
    // while avoiding unrelated words that merely contain the same letters.
    const remainder = word.split(slur).join('');
    if (remainder === '' || /^(?:(?:fuck|fucking|you|yo|u))+$/.test(remainder)) {
      return true;
    }
  }
  return false;
};

export const containsSevereAbuse = (value: unknown): boolean => {
  if (typeof value !== 'string' || !value.trim()) return false;

  const { sentence, words } = normalizeForMatching(value);
  if (words.some(word => SEVERE_SLUR_TOKENS.has(word) || word === 'kys')) {
    return true;
  }
  if (words.some(isJoinedSevereSlur)) {
    return true;
  }
  return [...TARGETED_THREAT_PATTERNS, ...TARGETED_PROFANITY_PATTERNS]
    .some(pattern => pattern.test(sentence));
};

export const redactSevereAbuse = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return containsSevereAbuse(value) ? SEVERE_ABUSE_REDACTION : value;
};

/**
 * Sanitize request-shaped API payloads at the shared boundary used by guest,
 * creator, profile, dashboard, and spot-request views.
 */
export const redactApplicationMessages = (payload: any): any => {
  if (Array.isArray(payload)) {
    return payload.map(item => redactApplicationMessages(item));
  }
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const sanitized = { ...payload };
  if (Object.prototype.hasOwnProperty.call(sanitized, 'message')) {
    sanitized.message = redactSevereAbuse(sanitized.message);
  }
  if (Array.isArray(sanitized.requests)) {
    sanitized.requests = sanitized.requests.map((item: any) => redactApplicationMessages(item));
  }
  if (Array.isArray(sanitized.items)) {
    sanitized.items = sanitized.items.map((item: any) => redactApplicationMessages(item));
  }
  return sanitized;
};
